import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, PenTool, Send, AlertTriangle, Crosshair, Tablet, Users, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { firmasAPI } from '../api';
import CancelarFirmaDialog from './CancelarFirmaDialog';
import ColocarCasillasDialog, { COLORES_CASILLA, puntosDe } from './firma/ColocarCasillasDialog';
import EnlaceFirmante from './firma/EnlaceFirmante';
import GrupoSolicitudes from './firma/GrupoSolicitudes';
import AnadirFirmanteDialog from './firma/AnadirFirmanteDialog';
import { toast } from '../hooks/use-toast';

const solapan = (a, b) => !(a[2] <= b[0] || b[2] <= a[0] || a[3] <= b[1] || b[3] <= a[1]);

const EnviarFirmaDialog = ({ adjunto, open, onOpenChange, onDone }) => {
  const [cargando, setCargando] = useState(false);
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [gestionar, setGestionar] = useState(null);
  const [integra, setIntegra] = useState(false);
  const [colocando, setColocando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [anadirA, setAnadirA] = useState(null);
  // [{ cliente_id, nombre, email, modo, email_destino, pagina, rect_rel }]
  const [firmantes, setFirmantes] = useState([]);

  const cargar = useCallback(async () => {
    if (!adjunto?.id) return;
    setCargando(true);
    setError('');
    try {
      const res = await firmasAPI.preparar(adjunto.id);
      setDatos(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo analizar el documento');
    } finally {
      setCargando(false);
    }
  }, [adjunto?.id]);

  useEffect(() => {
    if (open) {
      setMensaje('');
      setIntegra(false);
      setResultado(null);
      setFirmantes([]);
      setColocando(false);
      cargar();
    }
  }, [open, cargar]);

  const clientes = datos?.clientes || [];
  const multi = firmantes.length > 1;
  const unico = firmantes.length === 1 ? firmantes[0] : null;
  const maxPaginas = clientes.map((c) => c.ancla?.total_paginas).find((n) => n > 0) || 0;
  const sinCasilla = multi ? firmantes.filter((f) => !puntosDe(f).length) : [];
  const totalPuntos = firmantes.reduce((n, f) => n + puntosDe(f).length, 0);

  const alternarFirmante = (cliente) => {
    setResultado(null);
    setFirmantes((prev) => {
      if (prev.some((f) => f.cliente_id === cliente.id)) {
        return prev.filter((f) => f.cliente_id !== cliente.id);
      }
      return [...prev, {
        cliente_id: cliente.id,
        nombre: cliente.nombre,
        email: cliente.email || '',
        modo: 'email',
        email_destino: '',
        pagina: undefined,
        rect_rel: undefined,
        casillas: [],
      }];
    });
  };

  const actualizar = (clienteId, cambios) => setFirmantes((prev) =>
    prev.map((f) => (f.cliente_id === clienteId ? { ...f, ...cambios } : f)));

  // v135: varios puntos de firma por firmante. `pagina`/`rect_rel` siguen apuntando
  // al punto principal (el primero) para no romper el resto de la pantalla.
  const ponerCasilla = (clienteId, pos) => setFirmantes((prev) => prev.map((f) => {
    if (f.cliente_id !== clienteId) return f;
    const casillas = [...puntosDe(f), pos];
    return { ...f, casillas, pagina: casillas[0].pagina, rect_rel: casillas[0].rect_rel };
  }));

  const quitarCasilla = (clienteId, indice) => setFirmantes((prev) => prev.map((f) => {
    if (f.cliente_id !== clienteId) return f;
    const casillas = puntosDe(f).filter((_, i) => i !== indice);
    return { ...f, casillas,
             pagina: casillas[0]?.pagina, rect_rel: casillas[0]?.rect_rel };
  }));

  const validar = () => {
    if (!firmantes.length) {
      toast({ title: 'Elige al menos un firmante', variant: 'destructive' });
      return false;
    }
    if (multi && sinCasilla.length) {
      toast({ title: 'Faltan casillas de firma',
              description: `Coloca la casilla de ${sinCasilla.map((f) => f.nombre).join(', ')}`,
              variant: 'destructive' });
      return false;
    }
    if (multi) {
      for (let i = 0; i < firmantes.length; i += 1) {
        for (let j = i + 1; j < firmantes.length; j += 1) {
          const choque = puntosDe(firmantes[i]).some((a) => puntosDe(firmantes[j]).some(
            (b) => a.pagina === b.pagina && solapan(a.rect_rel, b.rect_rel)));
          if (choque) {
            toast({ title: 'Las casillas se solapan',
                    description: `Separa las de ${firmantes[i].nombre} y ${firmantes[j].nombre}`,
                    variant: 'destructive' });
            return false;
          }
        }
      }
    }
    const malos = firmantes.filter((f) => f.modo === 'email' && f.email_destino
                                          && !f.email_destino.includes('@'));
    if (malos.length) {
      toast({ title: 'Email no válido', description: `Revisa la dirección de ${malos[0].nombre}`,
              variant: 'destructive' });
      return false;
    }
    const sinDestino = firmantes.filter((f) => f.modo === 'email' && !f.email_destino && !f.email);
    if (sinDestino.length) {
      toast({ title: 'Falta el email',
              description: `${sinDestino[0].nombre} no tiene email en la ficha: escribe una dirección o elige «en el despacho»`,
              variant: 'destructive' });
      return false;
    }
    return true;
  };

  const enviar = async (forzar = false) => {
    if (!validar()) return;
    setEnviando(true);
    try {
      let res;
      if (multi) {
        res = await firmasAPI.solicitar({
          adjunto_id: adjunto.id,
          mensaje: mensaje.trim(),
          firma_integra: integra,
          enviar_email: true,
          forzar,
          firmantes: firmantes.map((f) => ({
            cliente_id: f.cliente_id,
            pagina: f.pagina,
            rect_rel: f.rect_rel,
            casillas: puntosDe(f),
            presencial: f.modo === 'despacho',
            email_destino: f.modo === 'despacho' ? '' : f.email_destino.trim(),
          })),
        });
      } else {
        const presencial = unico.modo === 'despacho';
        res = await firmasAPI.solicitar({
          adjunto_id: adjunto.id,
          cliente_id: unico.cliente_id,
          email_destino: presencial ? '' : (unico.email_destino.trim() || unico.email || ''),
          enviar_email: !presencial,
          presencial,
          mensaje: mensaje.trim(),
          pagina: unico.rect_rel?.length ? unico.pagina : -1,
          rect_rel: unico.rect_rel?.length ? unico.rect_rel : [],
          casillas: unico.rect_rel?.length ? puntosDe(unico) : [],
          firma_integra: integra,
          forzar,
        });
      }
      const lista = res.data?.firmantes
        || [{ ...res.data, presencial: unico ? unico.modo === 'despacho' : false }];
      setResultado({ ...res.data, lista });
      const pedidos = lista.filter((f) => !f.duplicado_existente).length;
      toast({
        title: pedidos === 0
          ? 'Ya había peticiones pendientes para esos firmantes'
          : (multi ? `Firma conjunta preparada para ${pedidos} firmante(s)` : 'Petición de firma creada'),
        description: multi
          ? 'El documento final llevará las firmas de todos: la actuación se crea al firmar el último'
          : (lista[0]?.email_enviado ? `Enviado a ${lista[0].email_enviado_a}` : 'Comparte el enlace o pasa la tablet'),
      });
      cargar();
      window.dispatchEvent(new Event('lexduo:firmas'));
      onDone?.();
    } catch (e) {
      toast({ title: 'No se pudo crear la petición',
              description: e?.response?.data?.detail || 'Error inesperado', variant: 'destructive' });
    } finally {
      setEnviando(false);
    }
  };

  const clientesFueraDeGrupo = (grupo) => {
    const dentro = new Set((grupo?.lista || []).map((s) => s.firmante_cliente_id).filter(Boolean));
    return clientes.filter((c) => !dentro.has(c.id));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="enviar-firma-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenTool className="w-5 h-5 text-indigo-600" />
            {multi ? 'Firma conjunta del documento' : 'Enviar a firmar al cliente'}
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            {multi
              ? 'Todos firman el MISMO documento, en cualquier orden. El PDF final llevará todas las firmas.'
              : 'Elige quién debe firmar. Puedes marcar varios clientes para una firma conjunta.'}
          </DialogDescription>
        </DialogHeader>

        {cargando && (
          <div className="py-10 flex items-center justify-center text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Analizando el documento…
          </div>
        )}

        {!cargando && error && (
          <div className="p-3 rounded bg-red-50 text-red-700 text-sm" data-testid="firma-error">{error}</div>
        )}

        {!cargando && !error && datos && (
          <div className="space-y-4">
            <div className="text-sm">
              <div className="font-medium text-gray-900 truncate">{datos.adjunto?.nombre}</div>
              <div className="text-gray-500 text-xs">
                {datos.expediente?.numero ? `Expediente ${datos.expediente.numero}` : ''}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">
                Firmantes <span className="text-red-600">*</span>
                {firmantes.length > 0 && (
                  <span className="ml-2 text-xs font-normal text-indigo-700" data-testid="firma-contador-firmantes">
                    {firmantes.length} seleccionado(s)
                  </span>
                )}
              </Label>
              {clientes.length === 0 ? (
                <div className="p-3 rounded bg-red-50 text-red-700 text-sm" data-testid="firma-sin-clientes">
                  Este expediente no tiene clientes asociados: añade el cliente en la ficha del
                  expediente o como participante con rol «cliente», «potencial cliente» u «otorgante».
                </div>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto" data-testid="firma-clientes-lista">
                  {clientes.map((c) => {
                    const marcado = firmantes.some((f) => f.cliente_id === c.id);
                    return (
                      <label key={c.id}
                             className={`flex items-start gap-2 rounded border p-2 text-sm cursor-pointer transition-colors ${
                               marcado ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={marcado}
                          onChange={() => alternarFirmante(c)}
                          data-testid={`firma-cliente-check-${c.id}`}
                        />
                        <span className="min-w-0">
                          <span className="block text-gray-900 truncate">{c.nombre}</span>
                          <span className="block text-xs text-gray-500 truncate">
                            {c.rol ? `${c.rol.replace('_', ' ')} · ` : ''}
                            {c.tiene_nif ? `DNI ${c.nif_parcial}` : 'sin DNI/NIF'}
                            {c.email ? ` · ${c.email}` : ' · sin email'}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {firmantes.length > 0 && (
              <div className="space-y-2" data-testid="firma-config-firmantes">
                {multi && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => setColocando(true)}
                            data-testid="firma-colocar-casillas-btn">
                      <Crosshair className="w-4 h-4 mr-2" /> Colocar los puntos de firma
                    </Button>
                    <span className={`text-xs ${sinCasilla.length ? 'text-amber-700' : 'text-green-700'}`}
                          data-testid="firma-casillas-estado">
                      {sinCasilla.length
                        ? `Obligatorio: faltan ${sinCasilla.length} de ${firmantes.length}`
                        : `${totalPuntos} punto(s) colocado(s) para ${firmantes.length} firmantes`}
                    </span>
                  </div>
                )}

                {firmantes.map((f, i) => {
                  const color = COLORES_CASILLA[i % COLORES_CASILLA.length];
                  const presencial = f.modo === 'despacho';
                  return (
                    <div key={f.cliente_id} className="rounded-md border border-gray-200 p-2 space-y-2"
                         data-testid={`firma-config-${f.cliente_id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2 text-sm font-medium text-gray-900 min-w-0">
                          {multi && <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${color.punto}`} />}
                          <span className="truncate">{f.nombre}</span>
                        </span>
                        <div className="flex gap-1 flex-shrink-0">
                          <button type="button" onClick={() => actualizar(f.cliente_id, { modo: 'email' })}
                                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] ${
                                    presencial ? 'border-gray-200 bg-white' : 'border-indigo-500 bg-indigo-50 font-medium'}`}
                                  data-testid={`firma-modo-email-${f.cliente_id}`}>
                            <Mail className="w-3 h-3" /> Email
                          </button>
                          <button type="button" onClick={() => actualizar(f.cliente_id, { modo: 'despacho' })}
                                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] ${
                                    presencial ? 'border-indigo-500 bg-indigo-50 font-medium' : 'border-gray-200 bg-white'}`}
                                  data-testid={`firma-modo-despacho-${f.cliente_id}`}>
                            <Tablet className="w-3 h-3" /> En el despacho
                          </button>
                        </div>
                      </div>

                      {presencial ? (
                        <p className="text-xs text-gray-500">
                          No se envía email y no tendrá que teclear su DNI: lo identifica el despacho en persona.
                        </p>
                      ) : (
                        <Input
                          className="h-8 text-xs"
                          placeholder={f.email ? `Email de la ficha: ${f.email}` : 'Sin email en la ficha: escribe una dirección'}
                          value={f.email_destino}
                          onChange={(e) => actualizar(f.cliente_id, { email_destino: e.target.value })}
                          data-testid={`firma-email-input-${f.cliente_id}`}
                        />
                      )}

                      <div className="flex items-center gap-2 text-xs flex-wrap">
                        <button type="button" className="underline text-gray-600"
                                onClick={() => setColocando(true)}
                                data-testid={`firma-casilla-btn-${f.cliente_id}`}>
                          {puntosDe(f).length ? 'Añadir o quitar puntos de firma' : 'Colocar su casilla'}
                        </button>
                        <span className={puntosDe(f).length ? 'text-indigo-700' : 'text-gray-400'}
                              data-testid={`firma-casilla-estado-${f.cliente_id}`}>
                          {puntosDe(f).length === 0
                            ? (multi ? 'sin colocar (obligatorio)' : 'opcional: si no, se coloca automáticamente')
                            : (puntosDe(f).length === 1
                              ? `1 punto · página ${puntosDe(f)[0].pagina + 1}`
                              : `${puntosDe(f).length} puntos de firma · páginas ${[...new Set(puntosDe(f).map((p) => p.pagina + 1))].join(', ')}`)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {multi && (
              <div className="flex gap-2 p-3 rounded bg-indigo-50 text-indigo-900 text-xs"
                   data-testid="firma-multifirma-aviso">
                <Users className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  Cada firma se estampa sobre la versión anterior, así que el documento final lleva
                  las {firmantes.length} firmas. La <strong>actuación</strong>, la copia al cliente, la copia
                  al despacho y la hoja de evidencias conjunta se generan <strong>al firmar el último</strong>.
                  Hasta entonces se registra el avance de cada firmante.
                </span>
              </div>
            )}

            {firmantes.length > 0 && (
              <div className="space-y-2">
                <Input
                  placeholder="Mensaje opcional para los firmantes (solo en los envíos por email)"
                  value={mensaje}
                  onChange={(e) => setMensaje(e.target.value)}
                  data-testid="firma-mensaje-input"
                />
                <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
                  <input type="checkbox" className="mt-0.5" checked={integra}
                         onChange={(e) => setIntegra(e.target.checked)}
                         data-testid="firma-integra-check" />
                  <span>
                    <strong>Firma íntegra del documento</strong>: además de la firma del final, se
                    estampa una firma reducida en el margen izquierdo de <strong>todas</strong> las páginas.
                  </span>
                </label>
              </div>
            )}

            {!multi && unico && clientes.find((c) => c.id === unico.cliente_id)?.tiene_nif === false && (
              <div className="p-3 rounded bg-amber-50 text-amber-900 text-sm" data-testid="firma-aviso-cliente">
                <strong>{unico.nombre}</strong> no tiene NIF/DNI en su ficha: podrá abrir el enlace
                <strong> sin identificarse</strong>. Añade el DNI en la ficha del contacto si quieres
                que se verifique su identidad.
              </div>
            )}

            {!multi && unico && !unico.rect_rel?.length && (() => {
              const ancla = clientes.find((c) => c.id === unico.cliente_id)?.ancla || {};
              return (
                <div className={`p-3 rounded text-sm ${ancla.detectada ? 'bg-indigo-50 text-indigo-900' : 'bg-amber-50 text-amber-900'}`}
                     data-testid="firma-ancla-info">
                  {ancla.detectada ? (
                    <>Punto de firma localizado en la <strong>página {(ancla.pagina ?? 0) + 1}</strong> de {ancla.total_paginas},
                      junto a «{ancla.texto}».</>
                  ) : (
                    <div className="flex gap-2">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{ancla.requiere_pagina_nueva
                        ? 'La última página está completa: se añadirá una hoja de firma al final del documento.'
                        : 'No se ha localizado un punto de firma en el texto: se propondrá un hueco libre y el cliente podrá moverlo.'}</span>
                    </div>
                  )}
                </div>
              );
            })()}

            {totalPuntos > firmantes.length && (
              <div className="p-3 rounded bg-indigo-50 text-indigo-900 text-sm"
                   data-testid="firma-varios-puntos-aviso">
                Documento con <strong>{totalPuntos} puntos de firma</strong>. Cada firmante firma
                <strong> una sola vez</strong> y su rúbrica se estampa en todos sus puntos; en los
                puntos adicionales se estampa reducida y sin el «Fdo.: nombre».
              </div>
            )}

            {resultado?.lista?.length > 0 && (
              <div className="space-y-2" data-testid="firma-resultado">
                <Label className="text-xs text-gray-500">
                  {resultado.lista.length > 1 ? 'Enlaces de firma por firmante' : 'Enlace de firma'}
                </Label>
                {resultado.lista.map((f) => (
                  <EnlaceFirmante key={f.id || f.token} firmante={f} />
                ))}
                {resultado.duplicado_existente && (
                  <Button size="sm" variant="outline" onClick={() => enviar(true)} disabled={enviando}
                          data-testid="firma-forzar-btn">
                    Crear peticiones nuevas igualmente
                  </Button>
                )}
              </div>
            )}

            <GrupoSolicitudes
              solicitudes={datos.solicitudes || []}
              onGestionar={setGestionar}
              onAnadirFirmante={setAnadirA}
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="firma-cerrar-btn">Cerrar</Button>
          <Button
            onClick={() => enviar(false)}
            disabled={enviando || cargando || !!error || !firmantes.length || (multi && sinCasilla.length > 0)}
            data-testid="firma-enviar-btn"
          >
            {enviando ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      : multi ? <Users className="w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {multi ? `Preparar firma conjunta (${firmantes.length})` : 'Preparar la firma'}
          </Button>
        </DialogFooter>

        <CancelarFirmaDialog
          firma={gestionar}
          open={!!gestionar}
          onOpenChange={(v) => { if (!v) setGestionar(null); }}
          onDone={() => { setGestionar(null); cargar(); onDone?.(); }}
        />

        <AnadirFirmanteDialog
          open={!!anadirA}
          onOpenChange={(v) => { if (!v) setAnadirA(null); }}
          baseFirmaId={anadirA?.base?.id}
          adjuntoId={adjunto?.id}
          clientes={anadirA ? clientesFueraDeGrupo(anadirA) : []}
          maxPaginas={maxPaginas}
          onDone={cargar}
        />
      </DialogContent>

      <ColocarCasillasDialog
        open={colocando}
        onOpenChange={setColocando}
        adjuntoId={adjunto?.id}
        firmantes={firmantes}
        maxPaginas={multi ? maxPaginas : 0}
        clientePreviewId={firmantes[0]?.cliente_id}
        onColocar={ponerCasilla}
        onQuitar={quitarCasilla}
      />
    </Dialog>
  );
};

export default EnviarFirmaDialog;
